import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthState } from '@/hooks/useAuthState';
import { BookingService } from '@/services/bookingService';
import { useToast } from '@/hooks/use-toast';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, Home, User, Mail, Phone, DollarSign, FileText, MapPin } from 'lucide-react';

interface Booking {
  id: number;
  property_id: number;
  guest_id: number;
  check_in: string;
  check_out: string;
  total_nights: number;
  total_price: number;
  status: string;
  guest_notes?: string;
  owner_notes?: string;
  created_at: string;
  updated_at: string;
  property_title: string;
  property_address: string;
  property_city: string;
  property_state: string;
  property_cover_image?: string;
  guest_name: string;
  guest_email: string;
  guest_phone?: string;
  owner_name: string;
  owner_email: string;
  owner_phone?: string;
  availability_period_id?: number;
}

const AdminViewBooking = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated, userType, userName, userPhoto, userId, loading: authLoading } = useAuthState();
  
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);

  // Formatar datas SEM timezone - usar exatamente como vem do banco
  // Para check_in e check_out (campos DATE): formatar diretamente da string YYYY-MM-DD
  const formatDate = (date: string | Date) => {
    if (!date) return "Data não informada";
    
    // Se for uma string no formato YYYY-MM-DD, formatar diretamente SEM criar Date object
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split('-');
      return `${day}/${month}/${year}`;
    }
    
    // Se for uma string ISO com hora, extrair apenas a parte da data
    if (typeof date === 'string' && date.includes('T')) {
      const datePart = date.substring(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        const [year, month, day] = datePart.split('-');
        return `${day}/${month}/${year}`;
      }
    }
    
    // Fallback para Date objects (não deve acontecer para check_in/check_out)
    if (date instanceof Date) {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${day}/${month}/${year}`;
    }
    
    return "Data inválida";
  };

  // Formatar data com dia da semana (para check-in e check-out)
  const formatDateWithWeekday = (date: string | Date) => {
    if (!date) return "Data não informada";
    
    const daysOfWeek = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];
    const months = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
    
    let year: string, month: string, day: string, weekday: number;
    
    // Se for uma string no formato YYYY-MM-DD, formatar diretamente
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      [year, month, day] = date.split('-');
      // Criar Date apenas para obter o dia da semana, usando UTC para evitar conversão
      const dateObj = new Date(`${year}-${month}-${day}T12:00:00Z`);
      weekday = dateObj.getUTCDay();
    } else if (typeof date === 'string' && date.includes('T')) {
      const datePart = date.substring(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        [year, month, day] = datePart.split('-');
        const dateObj = new Date(`${year}-${month}-${day}T12:00:00Z`);
        weekday = dateObj.getUTCDay();
      } else {
        return formatDate(date);
      }
    } else if (date instanceof Date) {
      year = String(date.getUTCFullYear());
      month = String(date.getUTCMonth() + 1).padStart(2, '0');
      day = String(date.getUTCDate()).padStart(2, '0');
      weekday = date.getUTCDay();
    } else {
      return formatDate(date);
    }
    
    const monthName = months[parseInt(month) - 1];
    const weekdayName = daysOfWeek[weekday];
    
    return `${weekdayName}, ${parseInt(day)} de ${monthName} de ${year}`;
  };

  // Formatar timestamp sem conversão de timezone (para created_at)
  const formatDateTime = (dateString: string | Date) => {
    if (!dateString) return "Data não informada";
    
    let date: Date;
    if (typeof dateString === 'string') {
      date = new Date(dateString);
    } else {
      date = dateString;
    }
    
    if (isNaN(date.getTime())) {
      return "Data inválida";
    }
    
    // Usar UTC para evitar conversão de timezone
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    
    const months = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
    const monthName = months[parseInt(month) - 1];
    
    return `${parseInt(day)} de ${monthName} de ${year}, às ${hours}:${minutes}`;
  };

  // Verificar se é admin (AdminLayout já faz isso)

  // Carregar dados da reserva
  useEffect(() => {
    const loadBooking = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        
        const bookingData = await BookingService.getBookingById(parseInt(id));
        
        if (!bookingData) {
          toast({
            title: "Erro",
            description: "Reserva não encontrada",
            variant: "destructive",
          });
          navigate('/admin/reservas');
          return;
        }
        
        setBooking(bookingData);
      } catch (error) {
        console.error('Erro ao carregar reserva:', error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar os dados da reserva",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadBooking();
  }, [id, toast, navigate]);

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'confirmed': return 'Confirmada';
      case 'pending': return 'Pendente';
      case 'cancelled': return 'Cancelada';
      case 'rejected': return 'Rejeitada';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'rejected': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando reserva...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!booking) {
    return null;
  }

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/admin/reservas')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Detalhes da Reserva</h1>
                <p className="text-muted-foreground">
                  ID: {booking.id}
                </p>
              </div>
            </div>
            <Badge className={getStatusColor(booking.status)}>
              {getStatusLabel(booking.status)}
            </Badge>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Informações do Imóvel */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Home className="h-5 w-5" />
                  Informações do Imóvel
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Título</p>
                  <p className="text-lg font-semibold">{booking.property_title}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Endereço
                  </p>
                  <p>{booking.property_address}</p>
                  <p className="text-muted-foreground">
                    {booking.property_city}, {booking.property_state}
                  </p>
                </div>
                <div>
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/imovel/${booking.property_id}`)}
                  >
                    Ver Imóvel
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Informações do Hóspede */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Informações do Hóspede
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Nome</p>
                  <p className="text-lg font-semibold">{booking.guest_name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email
                  </p>
                  <p>{booking.guest_email}</p>
                </div>
                {booking.guest_phone && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Telefone
                    </p>
                    <p>{booking.guest_phone}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Informações da Reserva */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Informações da Reserva
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Check-in</p>
                  <p className="text-lg font-semibold">
                    {formatDateWithWeekday(booking.check_in)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Check-out</p>
                  <p className="text-lg font-semibold">
                    {formatDateWithWeekday(booking.check_out)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Noites</p>
                  <p className="text-lg font-semibold">{booking.total_nights} noite(s)</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Data da Reserva</p>
                  <p>
                    {formatDateTime(booking.created_at)}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Informações Financeiras */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Informações Financeiras
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Valor Total</p>
                  <p className="text-2xl font-bold text-primary">
                    R$ {booking.total_price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Valor por Noite</p>
                  <p className="text-lg font-semibold">
                    R$ {(booking.total_price / booking.total_nights).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Observações */}
            {(booking.guest_notes || booking.owner_notes) && (
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Observações
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {booking.guest_notes && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-2">Observações do Hóspede</p>
                      <p className="bg-muted p-3 rounded-md">{booking.guest_notes}</p>
                    </div>
                  )}
                  {booking.owner_notes && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-2">Observações do Proprietário</p>
                      <p className="bg-muted p-3 rounded-md">{booking.owner_notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Informações do Proprietário */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Informações do Proprietário
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Nome</p>
                  <p className="text-lg font-semibold">{booking.owner_name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email
                  </p>
                  <p>{booking.owner_email}</p>
                </div>
                {booking.owner_phone && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Telefone
                    </p>
                    <p>{booking.owner_phone}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminViewBooking;

